import { ref } from 'vue'
import type MenuItem from '@/type/MenuItem'
import type { LifeDTO } from '../life/life'
import type { Router } from 'vue-router'

export type MenuCategory =
  | 'obituary'
  | 'none'
  | 'home'
  | 'lifepage'
  | 'schedule'
  | 'notification'
  | 'settings'
  | 'archive'
  | 'me'
  | 'history'
  | 'worship'
  | 'portrait'

export interface BackListener {
  /**
   *
   * @returns true - 可以back, false - 不可以back
   */
  handleBack: () => boolean

  afterBack: () => void
}

export class TitleBar {
  title: string
  showBack: boolean
  showHome: boolean
  showShare: boolean
  showClose: boolean
  transparent: boolean
  onBack?: () => void
  onHome?: () => void
  onShare?: () => void
  onClose?: () => void
  bgColor: string
  textColor: string
  titleAlign: string // center left
  iconColor: string // black white

  // Restored properties
  titleReverse: boolean = false
  backable: boolean = true
  menus: MenuItem[] = []
  category: MenuCategory = 'none'
  _life: LifeDTO | null = null
  _lifeId: string | null = null
  showMenuCount: number = 0
  reverse: boolean = false
  hideBack: boolean = false
  onMenuClicked: (item: MenuItem, node: Element | null) => void | null = () => null
  private backListeners: Array<BackListener> = []

  constructor(
    title: string,
    showBack: boolean,
    showHome: boolean,
    showShare: boolean,
    showClose: boolean,
    menus: MenuItem[],
    transparent: boolean,
    bgColor: string,
    textColor: string,
    titleAlign: string,
    iconColor: string,
    onBack?: () => void,
    onHome?: () => void,
    onShare?: () => void,
    onClose?: () => void
  ) {
    this.title = title
    this.showBack = showBack
    this.showHome = showHome
    this.showShare = showShare
    this.showClose = showClose
    this.transparent = transparent
    this.bgColor = bgColor
    this.menus = menus
    this.textColor = textColor
    this.titleAlign = titleAlign
    this.iconColor = iconColor
    this.onBack = onBack
    this.onHome = onHome
    this.onShare = onShare
    this.onClose = onClose
  }

  set lifeId(value: string | null) {
    this._lifeId = value
  }

  get lifeId(): string | null {
    return this._lifeId
  }

  set life(value: LifeDTO | null) {
    this._life = value
    if (value) {
      this._lifeId = value.id
    }
  }

  get life(): LifeDTO | null {
    return this._life
  }

  back(router?: Router) {
    let canBack = true
    this.backListeners.forEach((listener) => {
      if (!listener.handleBack()) {
        canBack = false
      }
    })

    if (canBack) {
      if (this.onBack) {
        this.onBack()
      } else if (router) {
        if (typeof window === 'undefined') return
        const historyState = (router.options.history as any)?.state
        if (historyState?.back) {
          router.go(-1)
        } else {
          router.replace('/home')
        }
      } else {
        if (typeof window === 'undefined') return
        if (window.location.pathname !== '/home') {
          window.location.replace('/home')
        }
      }
    }

    this.backListeners.forEach((listener) => {
      listener.afterBack()
    })
  }

  addBackListener(listener: BackListener) {
    this.backListeners.push(listener)
  }

  removeBackListener(listener: BackListener) {
    this.backListeners = this.backListeners.filter((l) => l !== listener)
  }
}

export function setTitleBar(bar: TitleBar) {
  _titleBar.value = bar
}

export function setTitle(title: string) {
  titleBar.value.title = title
}

export function setShowBack(showBack: boolean) {
  titleBar.value.showBack = showBack
}

export function setTransparent(transparent: boolean) {
  titleBar.value.transparent = transparent
}

export function setBgColor(color: string) {
  titleBar.value.bgColor = color
}

export function setTextColor(color: string) {
  titleBar.value.textColor = color
}

export function setIconColor(color: string) {
  titleBar.value.iconColor = color
}

export function setTitleAlign(align: string) {
  titleBar.value.titleAlign = align
}

export function setOnBack(onBack: () => void) {
  titleBar.value.onBack = onBack
}

export function setOnHome(onHome: () => void) {
  titleBar.value.onHome = onHome
}

export function setOnShare(onShare: () => void) {
  titleBar.value.onShare = onShare
}

export function setOnClose(onClose: () => void) {
  titleBar.value.onClose = onClose
}

export function resetTitleBar() {
  _titleBar.value = createDefaultTitleBar()
}

function createDefaultTitleBar() {
  const newBar = new TitleBar('', true, false, false, false, [], false, '#ffffff', '#000000', 'center', 'black')
  newBar.backable = true
  newBar.menus = []
  newBar.category = 'none'
  newBar.life = null
  newBar.showMenuCount = 0
  newBar.reverse = false
  newBar.hideBack = true
  newBar.titleReverse = false
  return newBar
}

const _titleBar = ref<TitleBar>(createDefaultTitleBar())
export const titleBar = _titleBar
